import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  apiError,
  composeQueryParams,
  mergeDshbPageMetadataObjects,
  paramsSerializer
} from "../../utils";
import { checkAuthStatusAsync } from "../auth/authSlice";

export const useLoadPlatformDataAndCurrentUser = () => {
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(window.location.search);
  const authToken = searchParams.get("authToken") || searchParams.get("token");

  useEffect(() => {
    let defaultSubdomain = process.env.REACT_APP_DEFAULT_PLATFORM_SLUG || "kmw";
    let subdomain;

    const ssrPlatformData = window.__tribe_platformData__;
    const isDevOrMainPreviewSite =
      window.location.host.includes("192.168.") ||
      window.location.host.includes("localhost") ||
      (window.location.host.includes("preview.tribesocial.io") &&
        !window.location.host.includes(".preview.tribesocial.io"));
    const isPreviewSiteSubdomain = window.location.host.includes(".preview.tribesocial.io");

    if (ssrPlatformData) dispatch(loadSSRPlatformData(ssrPlatformData));
    else if (isDevOrMainPreviewSite)
      dispatch(getPlatformDataAsync({ subdomain: defaultSubdomain, allowMarketingSurface: true }));
    else if (isPreviewSiteSubdomain) {
      subdomain = window.location.host.split(".")[0];
      dispatch(getPlatformDataAsync({ subdomain, allowMarketingSurface: false }));
    } else {
      // Real domains first resolve their own Platform. If that lookup misses,
      // load the node's default Platform so legacy product domains keep their
      // existing behavior, but do not authorize marketing for the fallback.
      const homepageUrl = window.location.hostname;
      dispatch(getPlatformDataByLiveDomainAsync({ homepageUrl, defaultSubdomain }));
    }

    dispatch(checkAuthStatusAsync({ token: authToken }));
  }, [dispatch, authToken]);
};

export function useCollectionPreviewBySlug(slug) {
  const collectionInitial = useSelector(selectCollectionBySlug(slug));
  const [collection, setCollection] = useState();
  const { id: platformId } = useSelector(state => state.frontend.platformData);

  useEffect(() => {
    setCollection(collectionInitial);
  }, [collectionInitial]);

  useEffect(() => {
    if (!collection && slug && platformId) {
      axios
        .get(`/api/collection-by-slug/${slug}/${platformId}?limit=20`)
        .then(resp => {
          setCollection(resp.data || {});
        })
        .catch(console.error);
    }
  }, [slug, collection, platformId]);

  return collection;
}

export function usePlatformSettings() {
  const { settings } = useSelector(state => state.frontend.platformData);
  return settings || {};
}

export function selectCollectionBySlug(slug) {
  return state => state.frontend.collections.find(c => c.slug === slug);
}

export const getPlatformDataAsync = createAsyncThunk("frontend/getPlatformData", async payload => {
  try {
    const { data: platformData } = await axios.get(`/api/platform/${payload.subdomain}`);
    initPlatformColors(platformData);
    return { platformData, allowMarketingSurface: payload.allowMarketingSurface };
  } catch (error) {
    console.log(error);
  }
});

export const getPlatformContentAsync = createAsyncThunk(
  "frontend/getPlatformContent",
  async payload => {
    try {
      const { platformData } = payload;
      const [topicData, groupData, tagData] = await Promise.all([
        axios.get(`/api/topics/${platformData?.id}`),
        axios.get(`/api/groups/platform/${platformData?.id}`),
        axios.get(`/api/tag/${platformData?.id}`)
      ]);

      return {
        groups: groupData.data,
        tags: tagData?.data,
        topics: topicData.data
      };
    } catch (error) {
      console.log(error);
    }
  }
);

export const getPublicCollectionsAsync = createAsyncThunk(
  "frontend/getPublicCollections",
  async (payload = {}, { getState }) => {
    const { platformData, metadata } = getState().frontend;
    const platformId = platformData?.id;
    const params = composeQueryParams(payload, metadata);
    const options = { params, paramsSerializer };
    const response = await axios.get(`/api/collection/public/${platformId}`, options);
    return { collections: response.data };
  }
);

export const getPlatformDataByLiveDomainAsync = createAsyncThunk(
  "frontend/getPlatformDataByLiveDomain",
  async payload => {
    try {
      const response = await axios.get(`/api/platform/by-homepage-url/${payload.homepageUrl}`);
      if (response.data) {
        initPlatformColors(response.data);
        return {
          platformData: response.data,
          platformByHomepageUrl: response.data,
          allowMarketingSurface: true
        };
      }
    } catch (error) {
      console.log(error);
    }

    try {
      const response = await axios.get(`/api/platform/${payload.defaultSubdomain}`);
      initPlatformColors(response.data);
      return {
        platformData: response.data,
        platformByHomepageUrl: null,
        allowMarketingSurface: false
      };
    } catch (error) {
      console.log(error);
      return {
        platformData: null,
        platformByHomepageUrl: null,
        allowMarketingSurface: false
      };
    }
  }
);

export const resetPlatformTokensAsync = createAsyncThunk(
  "frontend/tokens",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`/api/platform/tokens/${payload.slug}`);
      return { tokens: response.data };
    } catch (error) {
      return rejectWithValue(apiError(error));
    }
  }
);
export const getPlatformAnalyticsAsync = createAsyncThunk(
  "frontend/analytics",
  async (payload, payloadCreator) => {
    try {
      const { id } = payloadCreator.getState().frontend.platformData;
      const response = await axios.get(`/api/platform/analytics/${id}`);
      return { ...response.data };
    } catch (error) {
      return payloadCreator.rejectWithValue(apiError(error));
    }
  }
);

export const initialState = {
  loading: true,
  error: null,
  platformData: {},
  allowMarketingSurface: false,
  platformContent: {
    loading: true,
    tags: [],
    groups: [],
    topics: []
  },
  platformByHomepageUrl: {},
  analytics: {},
  collections: [],
  metadata: {
    currentPage: 0,
    itemsPerPage: 25,
    totalItems: 1,
    totalPages: 1,
    firstItem: 1,
    lastItem: 1,
    sort: null,
    filters: null,
    searchTerm: "",
    hasMore: true
  }
};

export const frontendSlice = createSlice({
  name: "FrontendCollections",
  initialState,
  reducers: {
    clearErrors: state => {
      state.error = null;
    },
    goToInfinitePaginationNextPage: state => {
      state.metadata.currentPage++;
    },
    loadSSRPlatformData: (state, action) => {
      state.platformData = action.payload;
      // SSR Platform data is only injected after the server resolves the
      // request hostname, so it is safe to use its marketing configuration.
      state.allowMarketingSurface = true;
      state.loading = false;
      state.error = null;
      initPlatformColors(action.payload);
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getPlatformDataAsync.pending, state => {
        state.loading = true;
      })
      .addCase(getPlatformDataAsync.fulfilled, (state, action) => {
        state.loading = false;

        state.platformData = action.payload && action.payload.platformData;
        if (!state.platformData) state.platformData = {};
        state.platformData.Topics = (action.payload && action.payload.topics) || [];

        if (typeof action.payload?.allowMarketingSurface === "boolean") {
          state.allowMarketingSurface = action.payload.allowMarketingSurface;
        }

        if (!state.platformData) {
          state.platformData = {};
        }
      })
      .addCase(getPlatformDataAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      })
      .addCase(getPublicCollectionsAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { items, ...metadata } = action.payload.collections || {};
        state.collections = [...state.collections, ...items];
        state.metadata = mergeDshbPageMetadataObjects(state.metadata, metadata);
      })
      .addCase(getPlatformContentAsync.pending, state => {
        state.platformContent.loading = true;
      })
      .addCase(getPlatformContentAsync.fulfilled, (state, action) => {
        state.platformContent.loading = false;

        state.platformContent = { ...state.platformContent, ...action.payload };
        if (!state.platformContent) {
          state.platformContent = {
            topics: [],
            tags: [],
            groups: []
          };
        }

        if (!state.platformContent.topics) {
          state.platformContent.topics = [];
        }
      })
      .addCase(getPlatformContentAsync.rejected, (state, action) => {
        state.platformContent.loading = false;
        state.error = action.error;
      })
      .addCase(getPlatformDataByLiveDomainAsync.pending, state => {
        state.loading = true;
      })
      .addCase(getPlatformDataByLiveDomainAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.platformByHomepageUrl = action.payload?.platformByHomepageUrl || {};
        state.platformData = action.payload?.platformData || {};
        state.platformData.Topics = state.platformData.Topics || [];
        state.allowMarketingSurface = action.payload?.allowMarketingSurface === true;
      })
      .addCase(getPlatformDataByLiveDomainAsync.rejected, (state, action) => {
        state.loading = false;
        state.allowMarketingSurface = false;
        state.error = action.error;
      })
      .addCase(resetPlatformTokensAsync.pending, state => {
        state.loading = true;
      })
      .addCase(resetPlatformTokensAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.platformData.basicToken = action.payload.tokens.basicToken;
        state.platformData.premiumToken = action.payload.tokens.premiumToken;
      })
      .addCase(getPlatformAnalyticsAsync.pending, state => {
        state.loading = true;
      })
      .addCase(getPlatformAnalyticsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.analytics = action.payload;
      });
  }
});
export const { clearErrors, goToInfinitePaginationNextPage, loadSSRPlatformData } =
  frontendSlice.actions;
export default frontendSlice.reducer;

function initPlatformColors(platformData) {
  const heroTextColor = platformData?.heroTextColor || "#333333";
  const primaryColor = platformData?.primaryColor || "#0000ff";
  const darkColor = platformData?.darkColor || "#333333";
  const lightColor = platformData?.lightColor || "#f3f3f3";

  const docStyle = document?.documentElement?.style;
  docStyle?.setProperty("--platform-color-hero-text", heroTextColor);
  docStyle?.setProperty("--platform-color-primary", primaryColor);
  docStyle?.setProperty("--platform-color-dark", darkColor);
  docStyle?.setProperty("--platform-color-light", lightColor);
}
